/* =========================================================
   BLOC 1 - IMPORTS
========================================================= */
import { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import { reactiverClientsInterclubIgnores } from "./ImportComptaTraitement";
import { preparerMappingInterclub, rapprochementAutomatiqueAutorise } from "./rapprochementInterclubPolicy";

export async function creerClubInterclubCanonique({ clientSupabase = supabase, client, groupe, clubs = [] }) {
  const mapping = preparerMappingInterclub({ client, clubs, groupe });
  const { data: clubCree, error: erreurCreation } = await clientSupabase
    .from("clubs")
    .insert({
      nom: client.nom_client,
      code: mapping.codeClient,
      ville: "",
      sport: "",
      groupe: mapping.groupe,
      numero_compte_interclub: mapping.codeClient
    })
    .select()
    .single();
  if (erreurCreation || !clubCree?.id) throw erreurCreation || new Error("Le Club créé ne possède aucun identifiant.");

  const { error: erreurCompte } = await clientSupabase
    .from("comptes_clients_interclub")
    .update({ club_id: clubCree.id, statut: "valide" })
    .eq("id", client.id)
    .eq("groupe", mapping.groupe)
    .select("id,club_id,statut")
    .single();
  if (!erreurCompte) return clubCree;

  const { error: erreurRollback } = await clientSupabase.rpc("crm_club_supprimer", {
    p_club_id: clubCree.id,
    p_compte_interclub_id: client.id
  });
  if (erreurRollback) {
    throw new Error(`Association Interclub échouée et rollback impossible : ${erreurCompte.message || erreurCompte}`);
  }
  throw erreurCompte;
}

export async function associerCompteInterclubCanonique({ clientSupabase = supabase, client, clubId, groupe, clubs = [] }) {
  const mapping = preparerMappingInterclub({ client, clubId, clubs, groupe });
  const ancienNumero = mapping.club?.numero_compte_interclub || null;
  const { error: erreurClub } = await clientSupabase
    .from("clubs")
    .update({ numero_compte_interclub: mapping.codeClient })
    .eq("id", clubId)
    .eq("groupe", mapping.groupe)
    .select("id,numero_compte_interclub")
    .single();
  if (erreurClub) throw erreurClub;

  const { error: erreurCompte } = await clientSupabase
    .from("comptes_clients_interclub")
    .update({ club_id: clubId, statut: "valide" })
    .eq("id", client.id)
    .eq("groupe", mapping.groupe)
    .select("id,club_id,statut")
    .single();
  if (!erreurCompte) return { clubId, ancienNumeroCompteInterclub: ancienNumero };

  const { error: erreurRollback } = await clientSupabase
    .from("clubs")
    .update({ numero_compte_interclub: ancienNumero })
    .eq("id", clubId)
    .eq("groupe", mapping.groupe)
    .select("id")
    .single();
  if (erreurRollback) {
    throw new Error(`Association Interclub échouée et rollback impossible : ${erreurCompte.message || erreurCompte}`);
  }
  throw erreurCompte;
}
/* =========================================================
   BLOC 2 - COMPOSANT PRINCIPAL
========================================================= */
function ClientsInterclubAValider({ userConnected, setPage, codeClientInitial = "" }) {

  const [clients, setClients] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [actionEnCours, setActionEnCours] = useState(false);
  const [nbIgnores, setNbIgnores] = useState(0);
  const [derniereAction, setDerniereAction] = useState(null);
  const [clientsSelectionnes, setClientsSelectionnes] = useState([]);
  const [filtreSuggestion, setFiltreSuggestion] = useState("tous");
  const [rechercheClient, setRechercheClient] = useState(String(codeClientInitial || ""));

  /* =========================================================
     BLOC 3 - CHARGEMENT INITIAL
  ========================================================= */
useEffect(() => {
  chargerClients();
  chargerClubs();

  // eslint-disable-next-line
}, []);
useEffect(() => {
  if (clients.length && clubs.length) {
    autoRapprocherClients();
  }
  // eslint-disable-next-line
}, [clients, clubs]);
  /* =========================================================
     BLOC 4 - CHARGER CLIENTS A VALIDER
  ========================================================= */
  async function chargerClients() {
    const { data, error } = await supabase
      .from("comptes_clients_interclub")
      .select("*")
      .eq("statut", "a_valider")
      .eq("groupe", userConnected?.groupe);

    if (error) {
      console.log("ERREUR CHARGEMENT CLIENTS :", error);
      return;
    }

    setClients(data || []);
	// 🔥 Comptage des ignorés
const { data: ignores } = await supabase
  .from("comptes_clients_interclub")
  .select("id")
  .eq("statut", "ignore")
  .eq("groupe", userConnected?.groupe);

setNbIgnores((ignores || []).length);
  }
/* =========================================================
   BLOC 4A - CHARGER CLUBS EXISTANTS AVEC CONTACT
========================================================= */
async function chargerClubs() {
  const { data: clubsData, error: clubsError } = await supabase
    .from("clubs")
    .select("id, code, nom, ville, numero_compte_interclub")
    .eq("groupe", userConnected?.groupe)
    .order("nom", { ascending: true });

  if (clubsError) {
    console.log("ERREUR CHARGEMENT CLUBS :", clubsError);
    return;
  }

  const { data: contactsData, error: contactsError } = await supabase
    .from("contacts")
    .select("club_id, nom")
    .order("nom", { ascending: true });

  if (contactsError) {
    console.log("ERREUR CHARGEMENT CONTACTS :", contactsError);
    return;
  }

  const premierContactParClub = {};

  (contactsData || []).forEach((contact) => {
    if (!premierContactParClub[contact.club_id] && contact.nom) {
      premierContactParClub[contact.club_id] = contact.nom;
    }
  });

  const clubsUniques = [];
  const nomsDejaVus = new Set();

  (clubsData || []).forEach((club) => {
    const cle = String(club.nom || "").trim().toLowerCase();

    if (!nomsDejaVus.has(cle)) {
      nomsDejaVus.add(cle);
      clubsUniques.push({
        ...club,
        premier_contact: premierContactParClub[club.id] || ""
      });
    }
  });

  setClubs(clubsUniques);
}
/* =========================================================
   BLOC 4B - SELECTION CLIENTS
========================================================= */
function basculerSelectionClient(clientId) {
  setClientsSelectionnes((selectionActuelle) => {
    if (selectionActuelle.includes(clientId)) {
      return selectionActuelle.filter((id) => id !== clientId);
    }

    return [...selectionActuelle, clientId];
  });
}

function toutCocherClients() {
  setClientsSelectionnes(clients.map((client) => client.id));
}

function toutDecocherClients() {
  setClientsSelectionnes([]);
}
/* =========================================================
   BLOC 5 - IGNORER CLIENT
========================================================= */
async function ignorerClient(id) {
  if (actionEnCours) return;

  setActionEnCours(true);

  try {
    const { error } = await supabase
      .from("comptes_clients_interclub")
      .update({ statut: "ignore" })
      .eq("id", id)
      .eq("groupe", userConnected?.groupe);

    if (error) {
      console.log("ERREUR IGNORE :", error);
      alert("Erreur suspension client");
      return;
    }

    await chargerClients();

  } finally {
    setActionEnCours(false);
  }
}
/* =========================================================
   BLOC 6 - CREER CLUB
========================================================= */
async function creerClub(client) {
  if (actionEnCours) return;

  setActionEnCours(true);

  try {
    const data = await creerClubInterclubCanonique({ client, groupe: userConnected?.groupe, clubs });

    setDerniereAction({
      type: "creation_club",
      clubId: data.id,
      clientId: client.id
    });

    await chargerClients();
    await chargerClubs();
  } catch (error) {
    console.log("ERREUR CREATION CLUB :", error);
    alert(error?.message || "Erreur création Club et mapping Interclub");
  } finally {
    setActionEnCours(false);
  }
}
/* =========================================================
   BLOC 7 - ASSOCIER A UN CLUB EXISTANT
========================================================= */
async function associerClub(client, clubId) {
  if (actionEnCours) return;

  setActionEnCours(true);

  try {
    const resultat = await associerCompteInterclubCanonique({ client, clubId, groupe: userConnected?.groupe, clubs });

    setDerniereAction({
      type: "association_club",
      clubId: clubId,
      clientId: client.id,
      ancienNumeroCompteInterclub: resultat.ancienNumeroCompteInterclub
    });

    await chargerClients();
    await chargerClubs();
  } catch (error) {
    console.log("ERREUR ASSOCIATION :", error);
    alert(error?.message || "Erreur association client");
  } finally {
    setActionEnCours(false);
  }
}
/* =========================================================
   BLOC 7C - ANNULER DERNIERE ACTION
========================================================= */
async function annulerDerniereAction() {
  if (!derniereAction || actionEnCours) return;

  const confirmer = window.confirm("Annuler la dernière action ?");
  if (!confirmer) return;

  setActionEnCours(true);

  try {
    if (derniereAction.type === "creation_club") {
      const { error } = await supabase.rpc("crm_club_supprimer", {
        p_club_id: derniereAction.clubId,
        p_compte_interclub_id: derniereAction.clientId
      });
      if (error) throw error;
    }

    if (derniereAction.type === "association_club") {
      await supabase
        .from("clubs")
        .update({
          numero_compte_interclub: derniereAction.ancienNumeroCompteInterclub
        })
        .eq("id", derniereAction.clubId)
        .eq("groupe", userConnected?.groupe);

      await supabase
        .from("comptes_clients_interclub")
        .update({
          club_id: null,
          statut: "a_valider"
        })
        .eq("id", derniereAction.clientId)
        .eq("groupe", userConnected?.groupe);
    }

    setDerniereAction(null);
    await chargerClients();
    await chargerClubs();

  } catch (error) {
    console.error("Annulation de création de club refusée :", error);
    alert(error?.message || "Impossible d’annuler la création du club.");
  } finally {
    setActionEnCours(false);
  }
}
/* =========================================================
   BLOC 7D - ACTIONS EN MASSE
========================================================= */
async function creerClubsSelectionnes() {
  if (actionEnCours || clientsSelectionnes.length === 0) return;

  const confirmer = window.confirm(
    `Créer ${clientsSelectionnes.length} club(s) ?`
  );
  if (!confirmer) return;

  setActionEnCours(true);

  try {
    const clientsACreer = clients.filter((client) =>
      clientsSelectionnes.includes(client.id)
    );

    const clubsConnus = [...clubs];
    const erreurs = [];
    for (const client of clientsACreer) {
      try {
        const clubCree = await creerClubInterclubCanonique({ client, groupe: userConnected?.groupe, clubs: clubsConnus });
        clubsConnus.push(clubCree);
      } catch (error) {
        console.log("ERREUR CREATION CLUB MASSE :", error);
        erreurs.push(`${client.code_client || client.nom_client} : ${error?.message || error}`);
      }
    }

    setClientsSelectionnes([]);
    setDerniereAction(null);
    await chargerClients();
    await chargerClubs();
    if (erreurs.length) alert(`Certaines créations ont été bloquées :\n${erreurs.join("\n")}`);

  } finally {
    setActionEnCours(false);
  }
}

async function ignorerClientsSelectionnes() {
  if (actionEnCours || clientsSelectionnes.length === 0) return;

  const confirmer = window.confirm(
    `Ignorer ${clientsSelectionnes.length} client(s) ?`
  );
  if (!confirmer) return;

  setActionEnCours(true);

  try {
    await supabase
      .from("comptes_clients_interclub")
      .update({ statut: "ignore" })
      .in("id", clientsSelectionnes)
      .eq("groupe", userConnected?.groupe);

    setClientsSelectionnes([]);
    setDerniereAction(null);
    await chargerClients();

  } finally {
    setActionEnCours(false);
  }
}

async function associerClientsSelectionnes() {
  if (actionEnCours || clientsSelectionnes.length === 0) return;

  const confirmer = window.confirm(
    `Associer ${clientsSelectionnes.length} client(s) aux clubs sélectionnés ?`
  );
  if (!confirmer) return;

  setActionEnCours(true);

  try {
    const clientsAAssocier = clients.filter((client) =>
      clientsSelectionnes.includes(client.id)
    );

    const clubsConnus = [...clubs];
    const erreurs = [];
    for (const client of clientsAAssocier) {
      const select = document.getElementById(`select-club-${client.id}`);
      const clubId = select?.value;

      if (!clubId) {
        alert(`Club manquant pour : ${client.nom_client}`);
        continue;
      }

      try {
        await associerCompteInterclubCanonique({ client, clubId, groupe: userConnected?.groupe, clubs: clubsConnus });
        const index = clubsConnus.findIndex((club) => String(club.id || club.identifiant) === String(clubId));
        if (index >= 0) clubsConnus[index] = { ...clubsConnus[index], numero_compte_interclub: client.code_client };
      } catch (error) {
        console.log("ERREUR ASSOCIATION MASSE :", error);
        erreurs.push(`${client.code_client || client.nom_client} : ${error?.message || error}`);
      }
    }

    setClientsSelectionnes([]);
    setDerniereAction(null);
    await chargerClients();
    await chargerClubs();
    if (erreurs.length) alert(`Certaines associations ont été bloquées :\n${erreurs.join("\n")}`);

  } finally {
    setActionEnCours(false);
  }
}
/* =========================================================
   BLOC 7A - SUGGESTION AUTOMATIQUE CLUB
========================================================= */
function normaliserTexte(valeur) {
  return String(valeur || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function trouverClubSuggere(client) {
  const nomClient = normaliserTexte(client.nom_client);

  if (!nomClient) return null;

  return clubs.find((club) => {
    const nomClub = normaliserTexte(club.nom);
    return nomClub.includes(nomClient) || nomClient.includes(nomClub);
  });
}
/* =========================================================
   BLOC 7B - AUTO RAPPROCHEMENT PAR CODE INTERCLUB
========================================================= */
async function autoRapprocherClients() {
  if (!clients.length || !clubs.length) return;

  for (const client of clients) {
    if (!rapprochementAutomatiqueAutorise(client.code_client)) continue;

    const clubMatch = clubs.find(
      (club) =>
        club.numero_compte_interclub &&
        String(club.numero_compte_interclub).trim() ===
          String(client.code_client).trim()
    );

    if (clubMatch) {
      console.log(
        "AUTO MATCH :",
        client.nom_client,
        "=>",
        clubMatch.nom
      );

      await supabase
        .from("comptes_clients_interclub")
        .update({
          club_id: clubMatch.id,
          statut: "valide"
        })
        .eq("id", client.id)
        .eq("groupe", userConnected?.groupe);
    }
  }

  // refresh après auto traitement
  chargerClients();
}
/* =========================================================
   BLOC 8 - RENDU
========================================================= */
return (
  <div style={{ padding: 20 }}>
    <button
      onClick={() => setPage("dashboard")}
      style={{
        marginBottom: 15,
        marginRight: 10,
        padding: "8px 14px",
        background: "#555",
        color: "white",
        border: "none",
        borderRadius: 6,
        cursor: "pointer"
      }}
    >
      ⬅ Retour dashboard
    </button>

    <h2>Clients Interclub à valider</h2>
<div
  style={{
    position: "sticky",
    top: 0,
    zIndex: 10,
    background: "white",
    padding: 10,
    marginBottom: 15,
    border: "1px solid #ddd",
    borderRadius: 8
  }}
>
  <strong>Clients cochés : {clientsSelectionnes.length}</strong>
<div style={{ marginTop: 10 }}>
  <label>
    Client :
    <input
      aria-label="Rechercher un client Interclub"
      value={rechercheClient}
      onChange={(e) => setRechercheClient(e.target.value)}
      placeholder="Code ou nom"
      style={{ marginLeft: 8, marginRight: 16, padding: 5 }}
    />
  </label>
  <label>
    Filtre :
    <select
      value={filtreSuggestion}
      onChange={(e) => setFiltreSuggestion(e.target.value)}
      style={{ marginLeft: 8, padding: 5 }}
    >
      <option value="tous">Tous</option>
      <option value="avec">Avec suggestion</option>
      <option value="sans">Sans suggestion</option>
    </select>
  </label>
</div>
  <div style={{ marginTop: 10 }}>
    <button onClick={toutCocherClients} disabled={actionEnCours}>
      Tout cocher
    </button>

    <button
      onClick={toutDecocherClients}
      disabled={actionEnCours}
      style={{ marginLeft: 8 }}
    >
      Tout décocher
    </button>

    <button
      onClick={associerClientsSelectionnes}
      disabled={actionEnCours || clientsSelectionnes.length === 0}
      style={{ marginLeft: 8 }}
    >
      Associer cochés
    </button>

    <button
      onClick={creerClubsSelectionnes}
      disabled={actionEnCours || clientsSelectionnes.length === 0}
      style={{ marginLeft: 8 }}
    >
      Créer cochés
    </button>

    <button
      onClick={ignorerClientsSelectionnes}
      disabled={actionEnCours || clientsSelectionnes.length === 0}
      style={{ marginLeft: 8 }}
    >
      Ignorer cochés
    </button>
  </div>
</div>
    {derniereAction && (
      <button
        onClick={annulerDerniereAction}
        disabled={actionEnCours}
        style={{
          marginBottom: 15,
          marginRight: 10,
          padding: "8px 14px",
          background: actionEnCours ? "#999" : "#000",
          color: "white",
          border: "none",
          borderRadius: 6,
          cursor: actionEnCours ? "not-allowed" : "pointer",
          opacity: actionEnCours ? 0.6 : 1
        }}
      >
        {actionEnCours ? "..." : "↩ Annuler dernière action"}
      </button>
    )}

    <div
      style={{
        marginBottom: 10,
        fontWeight: "bold",
        color: nbIgnores > 0 ? "#c62828" : "#2e7d32"
      }}
    >
      Ignorés en attente : {nbIgnores}
    </div>


    <button
      onClick={async () => {
        const ok = await reactiverClientsInterclubIgnores({
          groupe: userConnected?.groupe,
          supabaseClientActif: supabase
        });

        if (ok) {
          chargerClients();
        }
      }}
      style={{
        marginBottom: 15,
        padding: "8px 14px",
        background: "#f57c00",
        color: "white",
        border: "none",
        borderRadius: 6,
        cursor: "pointer"
      }}
    >
      🔄 Réactiver les ignorés
    </button>

    {clients.length === 0 && <div>Aucun client à valider 👍</div>}

    {clients
  .filter((client) => {
    const recherche = String(rechercheClient || "").trim().toLocaleLowerCase("fr-FR");
    if (recherche && !`${client.code_client || ""} ${client.nom_client || ""}`.toLocaleLowerCase("fr-FR").includes(recherche)) return false;
    const clubSuggere = trouverClubSuggere(client);

    if (filtreSuggestion === "avec") return !!clubSuggere;
    if (filtreSuggestion === "sans") return !clubSuggere;

    return true;
  })
  .map((client) => {
      const clubSuggere = trouverClubSuggere(client);

      return (
        <div
          key={client.id}
          style={{
            border: clientsSelectionnes.includes(client.id)
              ? "2px solid #1565c0"
              : "1px solid #ccc",
            padding: 15,
            marginBottom: 10,
            borderRadius: 8,
            background: clientsSelectionnes.includes(client.id)
              ? "#e3f2fd"
              : "white"
          }}
        >
          <label style={{ display: "block", marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={clientsSelectionnes.includes(client.id)}
              onChange={() => basculerSelectionClient(client.id)}
              disabled={actionEnCours}
              style={{ marginRight: 8 }}
            />
            Sélectionner ce client
          </label>

          <div>
            <strong>Code :</strong> {client.code_client}
          </div>

          <div>
            <strong>Nom :</strong> {client.nom_client}
          </div>

{clubSuggere && (
  <div
    style={{
      marginTop: 8,
      padding: "6px 10px",
      background: "#1565c0",
      color: "white",
      fontWeight: "bold",
      borderRadius: 5
    }}
  >
    {clubSuggere.code || "—"} -- {clubSuggere.nom} -- {clubSuggere.ville || "—"}
  </div>
)}
          <div style={{ marginTop: 10 }}>
            <select
              id={`select-club-${client.id}`}
              style={{
                marginRight: 10,
                padding: "6px",
                borderRadius: 5
              }}
            >
              <option value={clubSuggere ? clubSuggere.id : ""}>
                {clubSuggere
                  ? `Suggestion : ${clubSuggere.code || "—"} -- ${clubSuggere.nom}`
                  : "Choisir un club"}
              </option>

              {clubs
                .filter((club) => !club.numero_compte_interclub)
                .map((club) => (
                  <option key={club.id} value={club.id}>
                    {club.code || "—"} -- {club.nom} -- {club.ville || "—"} --{" "}
                    {club.premier_contact || "—"}
                  </option>
                ))}
            </select>

            <button
              onClick={() => {
                const select = document.getElementById(`select-club-${client.id}`);
                const clubId = select.value;

                if (!clubId) {
                  alert("Choisir un club");
                  return;
                }

                associerClub(client, clubId);
              }}
              disabled={actionEnCours}
              style={{
                marginRight: 10,
                padding: "6px 10px",
                background: actionEnCours ? "#999" : "#1565c0",
                color: "white",
                border: "none",
                borderRadius: 5,
                cursor: actionEnCours ? "not-allowed" : "pointer",
                opacity: actionEnCours ? 0.6 : 1
              }}
            >
              {actionEnCours ? "Action..." : "Associer"}
            </button>

            <button
              onClick={() => creerClub(client)}
              disabled={actionEnCours}
              style={{
                marginRight: 10,
                padding: "6px 10px",
                background: actionEnCours ? "#999" : "#2e7d32",
                color: "white",
                border: "none",
                borderRadius: 5,
                cursor: actionEnCours ? "not-allowed" : "pointer",
                opacity: actionEnCours ? 0.6 : 1
              }}
            >
              {actionEnCours ? "Action..." : "Créer club"}
            </button>

            <button
              onClick={() => ignorerClient(client.id)}
              disabled={actionEnCours}
              style={{
                padding: "6px 10px",
                background: actionEnCours ? "#999" : "#c62828",
                color: "white",
                border: "none",
                borderRadius: 5,
                cursor: actionEnCours ? "not-allowed" : "pointer",
                opacity: actionEnCours ? 0.6 : 1
              }}
            >
              {actionEnCours ? "Action..." : "Ignorer"}
            </button>
          </div>
        </div>
      );
    })}
  </div>
);
}
/* =========================================================
   BLOC 9 - EXPORT
========================================================= */
export default ClientsInterclubAValider;
